/*
 * DynamicIsland — iOS 14 Pro Dynamic Island'ın native taklidi.
 *
 * Tek mount edilir (DynamicIslandProvider). Her yerden `useDynamicIsland`
 * hook'u ile çağrılır:
 *
 *   const island = useDynamicIsland();
 *   island.show({ type: 'success', title: 'Sifariş göndərildi' });
 *   island.show({ type: 'cart', title: 'Səbətə əlavə edildi',
 *                 stockName: 'Pepperoni', quantity: 2, image: '...' });
 *   island.dismiss();
 *
 * Davranış (iOS native gibi):
 *   • Top-center, fixed, üstte siyah pill (light/dark fark etmez — iOS ile uyumlu)
 *   • Spring entrance, hafif squish + scale
 *   • Compact pill ↔ expanded card (tap to expand)
 *   • Drag-down (swipe to dismiss) eklenmiştir
 *   • Auto-dismiss 3-4s — type'a göre
 *   • Mesaj queue: bir mesaj ekrandayken yeni gelirse cross-fade ile yer değiştirir
 *
 * Tasarım hedefi: bağımsız, sticky, performans dostu, react-toastify yerine
 * QR menünün TÜM bildirimleri.
 */

import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { motion, AnimatePresence, PanInfo } from 'motion/react';
import {
  CheckCircle2,
  AlertTriangle,
  Info,
  ShoppingBag,
  BellRing,
  Loader2,
  Heart,
  Star,
  X,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type DynamicIslandType =
  | 'success'
  | 'error'
  | 'info'
  | 'warn'
  | 'loading'
  | 'cart'
  | 'waiter'
  | 'favorite'
  | 'rating';

export interface DynamicIslandPayload {
  /** UI tone — varsayılan 'info'. */
  type?: DynamicIslandType;
  /** Compact pill'in ana başlığı. KISA tutun (≤ 28 karakter). */
  title: string;
  /** Pill altında küçük ikincil satır. Opsiyonel. */
  message?: string;
  /** Sağ tarafta gösterilecek ikon yerine ürün görseli (cart için). */
  image?: string | null;
  /** cart variant'ı için ürün adı/qty. */
  stockName?: string;
  quantity?: number;
  /** Auto-dismiss süresi (ms). 0 → manuel dismiss bekle. */
  duration?: number;
  /** Tap ile genişlemeyi açıkça kapatmak istersen. */
  expandable?: boolean;
  /** Tap aksiyonu — expand yerine kullanıcı kendi callback'ini çalıştırabilir. */
  onPress?: () => void;
}

interface InternalNotice extends DynamicIslandPayload {
  id: number;
}

interface DynamicIslandContextValue {
  show: (payload: DynamicIslandPayload) => number;
  dismiss: (id?: number) => void;
}

const DynamicIslandContext = createContext<DynamicIslandContextValue | null>(null);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const DEFAULT_DURATION_BY_TYPE: Record<DynamicIslandType, number> = {
  success: 3000,
  info: 3000,
  warn: 3500,
  error: 4000,
  loading: 0, // manual dismiss
  cart: 3500,
  waiter: 3500,
  favorite: 2500,
  rating: 3000,
};

const haptic = (ms = 10) => {
  try {
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      (navigator as any).vibrate(ms);
    }
  } catch {
    /* noop */
  }
};

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export const DynamicIslandProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [notice, setNotice] = useState<InternalNotice | null>(null);
  const [expanded, setExpanded] = useState(false);
  const idCounter = useRef(0);
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (dismissTimerRef.current) {
      clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }
  }, []);

  const dismiss = useCallback(
    (id?: number) => {
      // Eğer specific id verildiyse sadece o id görünüyorken kapat.
      if (id != null) {
        setNotice((cur) => (cur && cur.id === id ? null : cur));
      } else {
        setNotice(null);
      }
      setExpanded(false);
      clearTimer();
    },
    [clearTimer],
  );

  const scheduleAutoDismiss = useCallback(
    (n: InternalNotice) => {
      clearTimer();
      const dur =
        n.duration != null
          ? n.duration
          : DEFAULT_DURATION_BY_TYPE[n.type || 'info'] ?? 3000;
      if (dur > 0) {
        dismissTimerRef.current = setTimeout(() => {
          // Sadece aynı id hâlâ aktif ise kapat — yeni mesaj geldiyse atla.
          setNotice((cur) => (cur && cur.id === n.id ? null : cur));
          setExpanded(false);
        }, dur);
      }
    },
    [clearTimer],
  );

  const show = useCallback(
    (payload: DynamicIslandPayload): number => {
      const id = ++idCounter.current;
      const next: InternalNotice = { ...payload, id };
      // Yeni gelen mesaj öncekini değiştirir — cross-fade istiyorsak motion'a
      // bırakabiliriz (AnimatePresence mode="wait" ya da key değişimi yapacak).
      haptic(8);
      setExpanded(false);
      setNotice(next);
      scheduleAutoDismiss(next);
      return id;
    },
    [scheduleAutoDismiss],
  );

  // Cleanup
  useEffect(() => () => clearTimer(), [clearTimer]);

  const value = useMemo<DynamicIslandContextValue>(
    () => ({ show, dismiss }),
    [show, dismiss],
  );

  return (
    <DynamicIslandContext.Provider value={value}>
      {children}
      <DynamicIslandHost
        notice={notice}
        expanded={expanded}
        setExpanded={(v) => {
          setExpanded(v);
          // Genişletildiğinde auto-dismiss timer'ı durdur — kullanıcı meşgul.
          if (v) clearTimer();
          else if (notice) scheduleAutoDismiss(notice);
        }}
        onDismiss={() => dismiss()}
      />
    </DynamicIslandContext.Provider>
  );
};

/**
 * Component'lerden kullanım:
 *   const island = useDynamicIsland();
 *   island.show({ type: 'success', title: 'Tamam!' });
 *
 * Provider yoksa konsola uyarı düşer ama crash etmez. Bu, QR menüde tek
 * yere mount ediyoruz; provider altında olmayan yan ekranlar (POS) uyarı
 * vermesin.
 */
export const useDynamicIsland = (): DynamicIslandContextValue => {
  const ctx = useContext(DynamicIslandContext);
  if (!ctx) {
    return {
      show: () => -1,
      dismiss: () => {},
    };
  }
  return ctx;
};

// ---------------------------------------------------------------------------
// Visual host — sticky black pill at top center (iOS Dynamic Island)
// ---------------------------------------------------------------------------

interface HostProps {
  notice: InternalNotice | null;
  expanded: boolean;
  setExpanded: (v: boolean) => void;
  onDismiss: () => void;
}

const DynamicIslandHost: React.FC<HostProps> = ({
  notice,
  expanded,
  setExpanded,
  onDismiss,
}) => {
  const handleDragEnd = useCallback(
    (_e: any, info: PanInfo) => {
      // Yukarı veya aşağı sürükleme → dismiss
      if (Math.abs(info.offset.y) > 30 || Math.abs(info.velocity.y) > 400) {
        onDismiss();
      }
    },
    [onDismiss],
  );

  return (
    <div
      className="pointer-events-none fixed top-2 left-0 right-0 z-[10000] flex justify-center px-3 sm:px-4"
      style={{
        // iPhone notch / safe area
        paddingTop: 'env(safe-area-inset-top, 0px)',
      }}
    >
      <AnimatePresence mode="wait">
        {notice && (
          <motion.div
            key={notice.id}
            // Sahte spring entrance — iOS feel
            initial={{ y: -60, scale: 0.5, opacity: 0 }}
            animate={{ y: 0, scale: 1, opacity: 1 }}
            exit={{ y: -40, scale: 0.6, opacity: 0 }}
            transition={{
              type: 'spring',
              stiffness: 380,
              damping: 26,
              mass: 0.6,
            }}
            drag="y"
            dragConstraints={{ top: 0, bottom: 20 }}
            dragElastic={0.4}
            onDragEnd={handleDragEnd}
            className="pointer-events-auto"
            style={{ touchAction: 'pan-y' }}
          >
            {expanded ? (
              <ExpandedCard notice={notice} onClose={onDismiss} />
            ) : (
              <CompactPill
                notice={notice}
                onPress={() => {
                  if (notice.onPress) {
                    notice.onPress();
                    onDismiss();
                    return;
                  }
                  // Eğer expandable false değilse tap → expand
                  if (notice.expandable !== false) {
                    haptic(6);
                    setExpanded(true);
                  } else {
                    onDismiss();
                  }
                }}
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Compact pill — iOS Dynamic Island core
// ---------------------------------------------------------------------------

const CompactPill: React.FC<{
  notice: InternalNotice;
  onPress: () => void;
}> = ({ notice, onPress }) => {
  const meta = getTypeMeta(notice.type || 'info');
  const showImage = notice.type === 'cart' && !!notice.image;

  return (
    <motion.button
      type="button"
      onClick={onPress}
      whileTap={{ scale: 0.97 }}
      // iOS pill: pure black, soft shadow, rounded-full, max 480px
      className="group relative flex items-center gap-3 px-3 sm:px-4 h-[44px] rounded-full bg-black text-white shadow-[0_8px_30px_rgba(0,0,0,0.45)] backdrop-blur-2xl select-none"
      style={{
        // Hafif inner glow (yansıtıcı), iOS Dynamic Island'da alt çizgi
        boxShadow:
          '0 8px 30px rgba(0,0,0,0.45), inset 0 0 0 0.5px rgba(255,255,255,0.06)',
        minWidth: 200,
        maxWidth: 480,
      }}
    >
      {/* Sol ikon kapsülü */}
      <span
        className={`flex items-center justify-center w-7 h-7 rounded-full shrink-0 ${meta.iconBg}`}
      >
        {notice.type === 'loading' ? (
          <Loader2 className={`w-4 h-4 ${meta.iconColor} animate-spin`} />
        ) : (
          <meta.Icon className={`w-4 h-4 ${meta.iconColor}`} />
        )}
      </span>

      {/* Orta — başlık + (varsa) kısa mesaj */}
      <span className="flex-1 min-w-0 text-left leading-tight">
        <span className="block text-[13px] font-bold tracking-tight truncate">
          {notice.title}
        </span>
        {notice.message && (
          <span className="block text-[11px] opacity-70 truncate">
            {notice.message}
          </span>
        )}
      </span>

      {/* Sağ — cart için ürün görseli, diğerleri için canlı dot */}
      {showImage ? (
        <span className="w-8 h-8 rounded-lg overflow-hidden shrink-0 ring-1 ring-white/10">
          <img
            src={notice.image as string}
            alt={notice.stockName || ''}
            className="w-full h-full object-cover"
          />
        </span>
      ) : notice.type === 'loading' ? null : (
        <motion.span
          aria-hidden
          className={`w-1.5 h-1.5 rounded-full ${meta.dot} shrink-0`}
          animate={{ opacity: [1, 0.4, 1], scale: [1, 1.3, 1] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
        />
      )}
    </motion.button>
  );
};

// ---------------------------------------------------------------------------
// Expanded card — tap to expand
// ---------------------------------------------------------------------------

const ExpandedCard: React.FC<{
  notice: InternalNotice;
  onClose: () => void;
}> = ({ notice, onClose }) => {
  const meta = getTypeMeta(notice.type || 'info');
  const isCart = notice.type === 'cart';

  return (
    <motion.div
      initial={{ scale: 0.96, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.96, opacity: 0 }}
      transition={{ type: 'spring', stiffness: 380, damping: 28 }}
      className="bg-black text-white rounded-[28px] shadow-[0_18px_50px_rgba(0,0,0,0.5)] backdrop-blur-2xl overflow-hidden"
      style={{
        boxShadow:
          '0 18px 50px rgba(0,0,0,0.5), inset 0 0 0 0.5px rgba(255,255,255,0.06)',
        minWidth: 280,
        maxWidth: 480,
      }}
    >
      <div className="flex items-start gap-3 p-4">
        <span
          className={`flex items-center justify-center w-10 h-10 rounded-full shrink-0 ${meta.iconBg}`}
        >
          {notice.type === 'loading' ? (
            <Loader2 className={`w-5 h-5 ${meta.iconColor} animate-spin`} />
          ) : (
            <meta.Icon className={`w-5 h-5 ${meta.iconColor}`} />
          )}
        </span>

        <div className="flex-1 min-w-0">
          <p className="text-[15px] font-bold leading-snug">{notice.title}</p>
          {notice.message && (
            <p className="text-[13px] opacity-70 leading-snug mt-0.5">
              {notice.message}
            </p>
          )}

          {isCart && (notice.stockName || notice.image) && (
            <div className="mt-3 flex items-center gap-3 bg-white/5 rounded-2xl p-2.5 ring-1 ring-white/5">
              {notice.image ? (
                <img
                  src={notice.image}
                  alt={notice.stockName || ''}
                  className="w-12 h-12 rounded-xl object-cover shrink-0"
                />
              ) : (
                <div className="w-12 h-12 rounded-xl bg-orange-500/20 flex items-center justify-center shrink-0">
                  🍽️
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-bold truncate">
                  {notice.stockName}
                </p>
                {notice.quantity != null && (
                  <p className="text-[11px] opacity-70">x{notice.quantity}</p>
                )}
              </div>
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={onClose}
          aria-label="close"
          className="w-7 h-7 rounded-full bg-white/10 hover:bg-white/15 flex items-center justify-center shrink-0 transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </motion.div>
  );
};

// ---------------------------------------------------------------------------
// Type → icon/color metadata
// ---------------------------------------------------------------------------

interface TypeMeta {
  Icon: React.ComponentType<{ className?: string }>;
  iconBg: string;
  iconColor: string;
  dot: string;
}

const getTypeMeta = (type: DynamicIslandType): TypeMeta => {
  switch (type) {
    case 'success':
      return {
        Icon: CheckCircle2,
        iconBg: 'bg-emerald-500/20',
        iconColor: 'text-emerald-400',
        dot: 'bg-emerald-400',
      };
    case 'error':
      return {
        Icon: AlertTriangle,
        iconBg: 'bg-red-500/20',
        iconColor: 'text-red-400',
        dot: 'bg-red-400',
      };
    case 'warn':
      return {
        Icon: AlertTriangle,
        iconBg: 'bg-amber-500/20',
        iconColor: 'text-amber-400',
        dot: 'bg-amber-400',
      };
    case 'loading':
      return {
        Icon: Loader2,
        iconBg: 'bg-blue-500/20',
        iconColor: 'text-blue-400',
        dot: 'bg-blue-400',
      };
    case 'cart':
      return {
        Icon: ShoppingBag,
        iconBg: 'bg-orange-500/20',
        iconColor: 'text-orange-400',
        dot: 'bg-orange-400',
      };
    case 'waiter':
      return {
        Icon: BellRing,
        iconBg: 'bg-indigo-500/20',
        iconColor: 'text-indigo-400',
        dot: 'bg-indigo-400',
      };
    case 'favorite':
      return {
        Icon: Heart,
        iconBg: 'bg-pink-500/20',
        iconColor: 'text-pink-400',
        dot: 'bg-pink-400',
      };
    case 'rating':
      return {
        Icon: Star,
        iconBg: 'bg-yellow-500/20',
        iconColor: 'text-yellow-400',
        dot: 'bg-yellow-400',
      };
    case 'info':
    default:
      return {
        Icon: Info,
        iconBg: 'bg-white/10',
        iconColor: 'text-white',
        dot: 'bg-white',
      };
  }
};

export default DynamicIslandProvider;
